import { Button, useTranslation } from '@hbs-us/ui';
import { useEffect, useState } from 'react';

import { useSession } from '../../auth/useSession.js';

const THIRTY_MIN_MS = 30 * 60 * 1000;
const TICK_MS = 1000;

function formatRemaining(startedAt: string, now: number): string {
  const started = new Date(startedAt).getTime();
  const remaining = Number.isNaN(started) ? 0 : Math.max(0, started + THIRTY_MIN_MS - now);
  const totalSeconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * The impersonation warning strip, rendered ABOVE the top bar by whichever shell
 * is live. Moved out of AppShell.tsx verbatim when the platform shell arrived so
 * both chrome variants mount the same banner — the timer, the aria-live status,
 * and the exit action are not allowed to differ per shell.
 */
export function ImpersonationBanner() {
  const { t } = useTranslation();
  const { session, exitImpersonation } = useSession();
  const [now, setNow] = useState(() => Date.now());
  const impersonation = session?.impersonation ?? null;

  useEffect(() => {
    if (!impersonation) return;
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [impersonation]);

  if (!impersonation) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex shrink-0 flex-wrap items-center justify-between gap-3 bg-destructive px-4 py-3 text-destructive-foreground"
    >
      <div className="min-w-0">
        <p className="font-semibold text-sm">
          {t('nav:impersonation.title', { tenant: session?.tenant.name ?? '' })}
        </p>
        <p className="mt-0.5 text-destructive-foreground/90 text-xs">
          {t('nav:impersonation.meta', {
            admin: impersonation.platformAdminEmail,
            time: formatRemaining(impersonation.startedAt, now),
          })}
        </p>
      </div>
      <Button type="button" variant="secondary" onClick={() => void exitImpersonation()}>
        {t('nav:impersonation.exit')}
      </Button>
    </div>
  );
}
